import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ApiCrudSection, {
  type Column,
  type FormField,
  type Row,
} from "../components/ApiCrudSection";
import {
  fetchContractors,
  createContractor,
  updateContractor,
  deleteContractor,
} from "../services/teamService";
import type { Contractor } from "../types";
import { useIsStaffAdmin } from "../../../hooks/useIsStaffAdmin";

// Maestro de contratistas (AgroCloud#60): alta/edición con sus datos, listado y
// búsqueda. Las tareas que ejecutan se administran en la sección "Tareas".
export default function ContractorsSection() {
  // #1007: solo quien administra modifica; los demás ven lo suyo en solo lectura.
  const isStaffAdmin = useIsStaffAdmin();
  const { t } = useTranslation("common");
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { items } = await fetchContractors();
      setRows(items as unknown as Row[]);
    } catch {
      setError(t("team.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const columns: Column[] = [
    { key: "name", label: t("team.fields.contractorName") },
    { key: "taxId", label: t("team.fields.taxId") },
    { key: "specialty", label: t("team.fields.specialty") },
    { key: "contact", label: t("team.fields.contact") },
    { key: "email", label: t("team.fields.email") },
    { key: "phone", label: t("team.fields.phone") },
    {
      key: "active",
      label: t("team.fields.active"),
      align: "center",
      render: (r) => (
        <span
          className={`rounded-full px-2 py-0.5 text-[11px] ${
            r.active === false
              ? "bg-gray-400/15 text-gray-500"
              : "bg-emerald-500/15 text-emerald-500"
          }`}
        >
          {r.active === false ? t("team.no") : t("team.si")}
        </span>
      ),
    },
  ];

  const fields: FormField[] = [
    { key: "name", label: t("team.fields.contractorName"), required: true },
    { key: "taxId", label: t("team.fields.taxId") },
    {
      key: "specialty",
      label: t("team.fields.specialty"),
      hint: t("team.hints.specialty"),
    },
    { key: "email", label: t("team.fields.email") },
    { key: "phone", label: t("team.fields.phone") },
    { key: "address", label: t("team.fields.address") },
    { key: "contact", label: t("team.fields.contact") },
    { key: "notes", label: t("team.fields.notes"), type: "textarea" },
  ];

  const toPayload = (values: Record<string, string>): Partial<Contractor> => ({
    name: values.name,
    taxId: values.taxId || null,
    specialty: values.specialty || null,
    email: values.email || null,
    phone: values.phone || null,
    address: values.address || null,
    contact: values.contact || null,
    notes: values.notes || null,
  });

  return (
    <ApiCrudSection
      readOnly={!isStaffAdmin}
      title={t("team.menu.contractors")}
      subtitle={t("team.contractorsSubtitle")}
      icon="ri-briefcase-line"
      columns={columns}
      fields={fields}
      rows={rows}
      loading={loading}
      error={error}
      searchKey="name"
      addLabel={t("team.newContractor")}
      onCreate={async (values) => {
        await createContractor(toPayload(values));
        await load();
      }}
      onUpdate={async (id, values) => {
        await updateContractor(id, toPayload(values));
        await load();
      }}
      onDelete={async (id) => {
        await deleteContractor(id);
        await load();
      }}
    />
  );
}
